/* eslint-disable max-len */
import PropTypes from 'prop-types'

import { Trip } from '../../models/trip/trip-enum'
import { controlFlagsPropType } from '../common/control-flags-prop-type'
import { idPropType } from '../common/id-prop-type'
import { iso8601PropType } from '../common/iso-8601-prop-type'
import { locationDescriptionPropType } from '../common/location-description-prop-type'
import { positionPropType } from '../common/position-prop-type'
import { fuelTypePropType } from '../vehicle/fuel-type-prop-type'
import { optiDriveScorePropType } from '../vehicle/opti-drive-score-prop-type'
import { tripModePropType } from './trip-mode-prop-type'
import { tripRulesPropType } from './trip-rules-prop-type'

const {
    AVERAGE_SPEED,
    AVG_SPEED,
    CARBON_FOOTPRINT,
    COASTING_DISTANCE,
    COASTING_REFERENCE_TIME,
    COASTING_TIME,
    COMMENT,
    CONSTANT_SPEED_REFERENCE_TIME_EXTRA_URBAN,
    CONSTANT_SPEED_REFERENCE_TIME_MOTORWAY,
    CONSTANT_SPEED_REFERENCE_TIME_URBAN,
    CONSTANT_SPEED_TIME_EXTRA_URBAN,
    CONSTANT_SPEED_TIME_MOTORWAY,
    CONSTANT_SPEED_TIME_URBAN,
    CONTACT,
    CONTROL_FLAGS,
    CRUISE_CONTROL_DISTANCE,
    CRUISE_CONTROL_PERCENT,
    DISTANCE,
    DRIVER_ID,
    DURATION,
    END_ADDRESS_ID,
    END_BATTERY_ENERGY,
    END_BATTERY_LEVEL,
    END_LOCATION_DESCRIPTION,
    END_ODOMETER,
    END_POSITION,
    END_TIME,
    FUEL_CONSUMPTION,
    FUEL_CONSUMPTION_REFERENCE,
    FUEL_TYPE,
    GREEN_SPEED_REFERENCE_TIME,
    GREEN_SPEED_TIME,
    HIGH_REVVING_AVERAGE,
    HIGH_REVVING_REFERENCE_TIME,
    HIGH_REVVING_TIME,
    IDLE_TIME,
    LAST_MODIFY_TIME,
    LAST_MODIFY_USER_NAME,
    LICENSE_PLATE_NO,
    MAX_SPEED,
    OBJECT_ID,
    OBJECT_NAME,
    OPTI_DRIVE_SCORE,
    PRIVATE_DISTANCE_PART,
    PURPOSE,
    RULES,
    START_ADDRESS_ID,
    START_BATTERY_ENERGY,
    START_BATTERY_LEVEL,
    START_LOCATION_DESCRIPTION,
    START_ODOMETER,
    START_POSITION,
    START_TIME,
    TRIP_ID,
    TRIP_MODE
} = Trip

/**
 * @typedef {Object} Trip
 *
 * @property {number}               [averageSpeed]                          - The average speed of the object during the trip (in kph).
 * @property {number}               [avgSpeed]                              - The average speed of the object during the trip (in kph).
 * @property {number}               [carbonFootprint]                       - The carbon footprint of the tri   p in grams.
 * @property {number}               [coastingDistance]                      - Distance with coasting (im meters).
 * @property {number}               [coastingReferenceTime]                 - Driving time with deceleration (in milliseconds). Used as reference for coastingTime.
 * @property {number}               [coastingTime]                          - Driving time with coasting (in milliseconds).
 * @property {string}               [comment]                               - The comment information of the trip.
 * @property {number}               [constantSpeedReferenceTimeExtraUrban]  - Total driving time in extra-urban areas (in milliseconds). Used as reference for constantSpeedTimeExtraUrban.
 * @property {number}               [constantSpeedReferenceTimeMotorway]    - Total driving time on motorways (in milliseconds). Used as reference for constantSpeedTimeMotorway.
 * @property {number}               [constantSpeedReferenceTimeUrban]       - Total driving time in urban areas (in milliseconds). Used as reference for constantSpeedTimeUrban.
 * @property {number}               [constantSpeedTimeExtraUrban]           - Driving time with constant speed in extra-urban areas (in milliseconds).
 * @property {number}               [constantSpeedTimeMotorway]             - Driving time with constant speed on motorways (in milliseconds).
 * @property {number}               [constantSpeedTimeUrban]                - Driving time with constant speed in urban areas (in milliseconds).
 * @property {string}               [contact]                               - The contact information of the trip.
 * @property {ControlFlags}         [controlFlags]                          - Control flags for current trip.
 * @property {number}               [cruiseControlDistance]                 - The cruise control distance
 * @property {number}               [cruiseControlPercent]                  - The cruise control percent
 * @property {number}               [distance]                              - The distance the object covered during the trip (in meters).
 * @property {Id}                   [driverId]                              - The ID of the driver.
 * @property {Id}                   [endAddressId]                          - The ID of the address at the endpoint of the trip.
 * @property {number}               [endBatteryEnergy]                      - End battery energy of the Trip (in Wh).
 * @property {number}               [endBatteryLevel]                       - End battery level of the Trip (in %).
 * @property {LocationDescription}  [endLocationDescription]                - The location description of the end point of the trip (new TT Reverse Geocoder).
 * @property {number}               [endOdometer]                           - The odometer of the object at the endpoint of the trip (in meters).
 * @property {Position}             [endPosition]                           - The geo position of the endpoint of the trip (decimal degree).
 * @property {ISO8601}              [endTime]                               - The end date of the trip (ISO8601).
 * @property {number}               [fuelConsumption]                       - The fuel consumed during the trip in milliliters.
 * @property {number}               [fuelConsumptionReference]              - The average fuel consumption of the corresponding vehicle in l/100km.
 * @property {FuelTypes}            [fuelType]                              - The fuel type used by the corresponding vehicle.
 * @property {number}               [greenSpeedReferenceTime]               - Driving time with proposed green speed (milliseconds).
 * @property {number}               [greenSpeedTime]                        - Actual time of driving with proposed green speed (milliseconds).
 * @property {number}               [highRevvingAverage]                    - Average high-revving amount (in rpm).
 * @property {number}               [highRevvingReferenceTime]              - Total driving time used as reference for high-revving calculations (in milliseconds).
 * @property {number}               [highRevvingTime]                       - Actual driving time with high-revving (in milliseconds).
 * @property {number}               [idleTime]                              - The amount of time the object idled (in seconds).
 * @property {ISO8601}              [lastModifyTime]                        - The last modify time of the trip (ISO8601).
 * @property {string}               [lastModifyUserName]                    - The last modify user name of the trip.
 * @property {string}               [licensePlateNo]                        - The object's license plate number
 * @property {number}               [maxSpeed]                              - The top speed of the object during the trip (in kph).
 * @property {Id}                   [objectId]                              - The ID of the object.
 * @property {string}               [objectName]                            - The object's name
 * @property {OptiDriveScore}       [optiDriveScore]                        - The OptiDrive score of the trip, including component scores.
 * @property {number}               [privateDistancePart]                   - Part of the trip distance that was private.
 * @property {string}               [purpose]                               - The purpose of the trip.
 * @property {Id}                   [startAddressId]                        - The ID of the address at the starting point of the trip.
 * @property {number}               [startBatteryEnergy]                    - Start battery energy of the Trip (in Wh).
 * @property {number}               [startBatteryLevel]                     - Start battery level of the Trip (in %).
 * @property {LocationDescription}  [startLocationDescription]              - The location description of the start point of the trip (new TT Reverse Geocoder).
 * @property {number}               [startOdometer]                         - The odometer of the object at the beginning of the trip (in meters).
 * @property {Position}             [startPosition]                         - The geo position of the starting point of the trip (decimal degree).
 * @property {ISO8601}              [startTime]                             - The start date of the trip (ISO8601).
 * @property {Id}                   [tripId]                                - The id of the trip.
 * @property {TripModes}            [tripMode]                              - The trip mode of the trip.
 * @property {number}               [duration]                              - The duration of the trip in seconds.
 */
export const tripPropType = PropTypes.shape({
    [AVERAGE_SPEED]: PropTypes.number,
    [AVG_SPEED]: PropTypes.number,
    [CARBON_FOOTPRINT]: PropTypes.number,
    [COASTING_DISTANCE]: PropTypes.number,
    [COASTING_REFERENCE_TIME]: PropTypes.number,
    [COASTING_TIME]: PropTypes.number,
    [COMMENT]: PropTypes.string,
    [CONSTANT_SPEED_REFERENCE_TIME_EXTRA_URBAN]: PropTypes.number,
    [CONSTANT_SPEED_REFERENCE_TIME_MOTORWAY]: PropTypes.number,
    [CONSTANT_SPEED_REFERENCE_TIME_URBAN]: PropTypes.number,
    [CONSTANT_SPEED_TIME_EXTRA_URBAN]: PropTypes.number,
    [CONSTANT_SPEED_TIME_MOTORWAY]: PropTypes.number,
    [CONSTANT_SPEED_TIME_URBAN]: PropTypes.number,
    [CONTACT]: PropTypes.string,
    [CONTROL_FLAGS]: controlFlagsPropType,
    [CRUISE_CONTROL_DISTANCE]: PropTypes.number,
    [CRUISE_CONTROL_PERCENT]: PropTypes.number,
    [DISTANCE]: PropTypes.number,
    [DRIVER_ID]: idPropType,
    [DURATION]: PropTypes.number,
    [END_ADDRESS_ID]: idPropType,
    [END_BATTERY_ENERGY]: PropTypes.number,
    [END_BATTERY_LEVEL]: PropTypes.number,
    [END_LOCATION_DESCRIPTION]: locationDescriptionPropType,
    [END_ODOMETER]: PropTypes.number,
    [END_POSITION]: positionPropType,
    [END_TIME]: iso8601PropType,
    [FUEL_CONSUMPTION]: PropTypes.number,
    [FUEL_CONSUMPTION_REFERENCE]: PropTypes.number,
    [FUEL_TYPE]: fuelTypePropType,
    [GREEN_SPEED_REFERENCE_TIME]: PropTypes.number,
    [GREEN_SPEED_TIME]: PropTypes.number,
    [HIGH_REVVING_AVERAGE]: PropTypes.number,
    [HIGH_REVVING_REFERENCE_TIME]: PropTypes.number,
    [HIGH_REVVING_TIME]: PropTypes.number,
    [IDLE_TIME]: PropTypes.number,
    [LAST_MODIFY_TIME]: iso8601PropType,
    [LAST_MODIFY_USER_NAME]: PropTypes.string,
    [LICENSE_PLATE_NO]: PropTypes.string,
    [MAX_SPEED]: PropTypes.number,
    [OBJECT_ID]: idPropType,
    [OBJECT_NAME]: PropTypes.string,
    [OPTI_DRIVE_SCORE]: optiDriveScorePropType,
    [PRIVATE_DISTANCE_PART]: PropTypes.number,
    [PURPOSE]: PropTypes.string,
    [RULES]: tripRulesPropType,
    [START_ADDRESS_ID]: idPropType,
    [START_BATTERY_ENERGY]: PropTypes.number,
    [START_BATTERY_LEVEL]: PropTypes.number,
    [START_LOCATION_DESCRIPTION]: locationDescriptionPropType,
    [START_ODOMETER]: PropTypes.number,
    [START_POSITION]: positionPropType,
    [START_TIME]: iso8601PropType,
    [TRIP_ID]: idPropType,
    [TRIP_MODE]: tripModePropType
})
